import { useState, useEffect, useCallback, useRef } from "react";
import { useIsaiah } from "./IsaiahPlacementManager";

/**
 * IsaiahContextEngine v1
 * The "brain" behind Isaiah's smart behavior.
 * Monitors user activity and triggers contextual reactions at the right moments.
 *
 * Tracks:
 * - Inactivity (no clicks/scrolls for X seconds)
 * - Session visit count per page (first visit vs return)
 * - Time spent on page
 * - Recent interaction count (activity bursts)
 *
 * Anti-clutter:
 * - Global cooldown between companion messages
 * - Per-context cooldown (don't repeat same category)
 * - Max messages per session to avoid fatigue
 * - Gradual frequency reduction over time
 */

const INACTIVITY_THRESHOLD_MS = 25000; // 25 seconds of no interaction
const COOLDOWN_MS = 12000; // Minimum 12s between companion messages
const MAX_MESSAGES_PER_PAGE = 4; // Max context-triggered messages per page visit
const SESSION_KEY = "isaiah_context_session";

// Contextual dialogue for smart triggers
const CONTEXT_DIALOGUE = {
  inactivity: {
    home: [
      "Psst... the worlds are waiting!",
      "Want me to show you around?",
      "There's so much to explore!",
    ],
    arcade: [
      "Pick a game, I'll cheer you on!",
      "Don't just watch, let's PLAY!",
      "The high scores aren't gonna beat themselves!",
    ],
    adventure: [
      "The trail's getting cold... let's move!",
      "There's treasure out there somewhere!",
      "An explorer never stands still for long!",
    ],
    creator: [
      "Your canvas is waiting for greatness!",
      "Even one object is a start!",
      "The best worlds start with a single idea!",
    ],
    learn: [
      "Pick a subject, I'm right here with you!",
      "Knowledge grows when you take the first step!",
      "The forest has so many paths to explore!",
    ],
    default: [
      "Still here? Let's do something fun!",
      "Tap anything to get started!",
      "I'm here whenever you're ready!",
    ],
  },
  firstVisit: {
    home: ["Welcome to Isaiah's World! I'll be your guide!"],
    arcade: ["First time in Arcade City? You're gonna love it!"],
    adventure: ["A brand-new adventurer! The island welcomes you!"],
    creator: ["Welcome to Creator Mode! Let your imagination run wild!"],
    learn: ["First time in the Learning Forest? Let's explore together!"],
    default: ["Hey, this is new! Let me show you around!"],
  },
  returnVisit: {
    home: ["Welcome back! Ready for another round?"],
    arcade: ["Back for more? Let's set some records!"],
    adventure: ["The adventurer returns! New mysteries await!"],
    creator: ["Ready to build again? The canvas missed you!"],
    learn: ["Back to learn more? Let's keep growing!"],
    default: ["Good to see you again!"],
  },
  milestone: [
    "Look how far you've come!",
    "You're making amazing progress!",
    "I'm so proud of you!",
    "You're becoming a pro!",
    "Incredible journey so far!",
  ],
  streak: [
    "You're on a streak! Keep going!",
    "Unstoppable! Don't stop now!",
    "That's consecutive greatness!",
    "Streak mode: ACTIVATED!",
  ],
  encourageReturn: [
    "Miss you already, come back soon!",
    "There's always something new waiting!",
    "The worlds will be here when you're ready!",
  ],
};

const getRandomMessage = (messages) => {
  if (!messages || messages.length === 0) return null;
  return messages[Math.floor(Math.random() * messages.length)];
};

const getSessionData = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : { visitCounts: {}, messageCount: 0 };
  } catch {
    return { visitCounts: {}, messageCount: 0 };
  }
};

const saveSessionData = (data) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
  } catch {
    // Storage full or unavailable
  }
};

/**
 * useIsaiahContextEngine
 * Hook that provides smart, behavior-aware Isaiah interactions.
 * Attach to any page/component to get automatic contextual reactions.
 */
export const useIsaiahContextEngine = () => {
  const { pageContext, showCompanionMessage, isCompanionVisible, isCompanionHidden } = useIsaiah();
  const [isInactive, setIsInactive] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const cooldownRef = useRef(0);
  const messageCountRef = useRef(0);
  const inactivityTimerRef = useRef(null);
  const visitRecordedRef = useRef(false);

  // Track user activity
  const recordActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIsInactive(false);
  }, []);

  // Send a context-aware message (respects cooldown and limits)
  const sendContextMessage = useCallback((category, customContext) => {
    const now = Date.now();
    if (now - cooldownRef.current < COOLDOWN_MS) return false;
    if (messageCountRef.current >= MAX_MESSAGES_PER_PAGE) return false;
    if (isCompanionHidden) return false;

    const context = customContext || pageContext || "default";
    const pool = CONTEXT_DIALOGUE[category];
    let message;

    if (pool && typeof pool === "object" && !Array.isArray(pool)) {
      // Category has context-specific messages
      message = getRandomMessage(pool[context] || pool.default);
    } else if (Array.isArray(pool)) {
      message = getRandomMessage(pool);
    }

    if (!message) return false;

    showCompanionMessage(message, 6000);
    cooldownRef.current = now;
    messageCountRef.current += 1;
    return true;
  }, [pageContext, showCompanionMessage, isCompanionHidden]);

  // Inactivity detection
  useEffect(() => {
    const checkInactivity = () => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= INACTIVITY_THRESHOLD_MS && !isInactive && isCompanionVisible) {
        setIsInactive(true);
        sendContextMessage("inactivity");
      }
    };

    inactivityTimerRef.current = setInterval(checkInactivity, 5000);

    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach(e => window.addEventListener(e, recordActivity, { passive: true }));

    return () => {
      if (inactivityTimerRef.current) clearInterval(inactivityTimerRef.current);
      events.forEach(e => window.removeEventListener(e, recordActivity));
    };
  }, [isInactive, isCompanionVisible, recordActivity, sendContextMessage]);

  // First visit / return visit detection
  useEffect(() => {
    if (visitRecordedRef.current) return;
    visitRecordedRef.current = true;

    const session = getSessionData();
    const visitKey = pageContext || "default";
    const visits = session.visitCounts[visitKey] || 0;

    // Update visit count
    session.visitCounts[visitKey] = visits + 1;
    saveSessionData(session);

    // First visit to this context in this session
    if (visits === 0) {
      // Delay to not overlap with welcome reactions
      const timer = setTimeout(() => {
        sendContextMessage("firstVisit");
      }, 5000);
      return () => clearTimeout(timer);
    }

    // Return visit (been here before this session)
    if (visits >= 1 && visits <= 3) {
      const timer = setTimeout(() => {
        sendContextMessage("returnVisit");
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [pageContext, sendContextMessage]);

  // Reset per-page counters when context changes
  useEffect(() => {
    messageCountRef.current = 0;
    visitRecordedRef.current = false;
    lastActivityRef.current = Date.now();
    setIsInactive(false);
  }, [pageContext]);

  return {
    isInactive,
    recordActivity,
    sendContextMessage,
    triggerMilestone: () => sendContextMessage("milestone"),
    triggerStreak: () => sendContextMessage("streak"),
  };
};

export default useIsaiahContextEngine;
